"use client";

import { Container, Typography, Box, Paper, Button, useTheme } from "@mui/material";
import EnrollmentHeader from "@/components/EnrollmentHeader";
import EnrollmentFooter from "@/components/EnrollmentFooter";
import { usePageTitle } from "@/hooks/usePageTitle";
import { resolveStepDisabled, useStudyStatus } from "@/hooks/useStudyStatus";

export default function CadaPortalPage() {
    const theme = useTheme();
    const tenant = "cada";

    usePageTitle("Dealer Portal", tenant);

    const { stepOverrides } = useStudyStatus(tenant);
    const enrollDisabled = resolveStepDisabled(stepOverrides.enroll, false);
    const editParticipantsDisabled = resolveStepDisabled(stepOverrides.edit_participants, false);
    const surveyDisabled = resolveStepDisabled(stepOverrides.survey, false);
    const uploadPayrollDisabled = resolveStepDisabled(stepOverrides.upload_payroll, false);

    return (
        <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
            <EnrollmentHeader tenant={tenant} />

            {/* Main content */}
            <Container maxWidth="lg" sx={{ py: 4 }} component="main" role="main">
                <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
                    Dealer Portal
                </Typography>

                {/* Step 1: Enroll */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step1-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step1-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 1: Enroll
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Look up your dealership using your CADA Company ID
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Confirm your dealership info and accept the CADA terms of
                                        agreement
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Receive enrollment confirmation email
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/enrollment"
                                aria-label={enrollDisabled ? "Enrollment is closed" : "Go to enrollment page"}
                                disabled={enrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Enroll Now
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 2: Confirm Your Participating Dealership Info */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step2-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step2-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 2: Confirm Your Participating Dealership Info
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2">
                                        Add or edit participating dealerships and franchise info
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/edit-participants"
                                aria-label={editParticipantsDisabled ? "Edit participants is closed" : "Go to edit participants page"}
                                disabled={editParticipantsDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Add/Edit Dealerships
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 3: The Questionnaire */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step3-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step3-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 3: The Questionnaire
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Complete the online questionnaire with your benefits and
                                        compensation details
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/survey"
                                aria-label={surveyDisabled ? "Questionnaire is closed" : "Go to survey page"}
                                disabled={surveyDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Start Questionnaire
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Step 4: Upload Your Payroll File */}
                <Paper
                    elevation={2}
                    sx={{ mb: 3 }}
                    component="section"
                    aria-labelledby="step4-title"
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: { xs: "column", md: "row" },
                        }}
                    >
                        <Box sx={{ flex: 1, p: 3 }}>
                            <Typography
                                id="step4-title"
                                variant="h6"
                                component="h2"
                                sx={{ mb: 2, fontWeight: 600, color: theme.palette.primary.main }}
                            >
                                Step 4: Upload Your Payroll File
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Extract your data from your payroll system
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Complete the payroll template with data for ALL employees
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Include all employees on payroll as of December 31, 2025
                                    </Typography>
                                </li>
                                <li>
                                    <Typography variant="body2">
                                        Upload your completed payroll file
                                    </Typography>
                                </li>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 3,
                                borderLeft: { md: 1 },
                                borderTop: { xs: 1, md: 0 },
                                borderColor: "divider",
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                href="/upload"
                                aria-label={uploadPayrollDisabled ? "Payroll upload is closed" : "Go to upload page"}
                                disabled={uploadPayrollDisabled}
                                sx={{ minWidth: 232, height: 40 }}
                            >
                                Upload Payroll
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Closing message */}
                <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, textAlign: "center", my: 4 }}
                    role="alert"
                    aria-live="polite"
                >
                    All data must be submitted by March 30, 2026
                </Typography>

                <EnrollmentFooter tenant={tenant} />
            </Container>
        </Box>
    );
}
